import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { Subject, BehaviorSubject, throwError } from 'rxjs';
import { InjectionDataModel } from '../_models/injection-data-model';
import { InjectionDataRecord } from '../_models/injection-data-record';
import { catchError } from 'rxjs/operators';
import { InjectionDataBatch } from '../_models/injection-data-batch';
import { APIOperationResult } from '../_models/apioperation-result';
import { DeleteDateRangeModel } from '../_models/delete-date-range-model';
import { UserSubmittableDataBatch } from '../_models/user-submittable-data-batch';

import { UserSubmittableService } from './user-submittable-service';

@Injectable({
  providedIn: 'root'
})
export class InjectionService extends UserSubmittableService {

  public currentInjectionBatch: BehaviorSubject<InjectionDataBatch> = new BehaviorSubject<InjectionDataBatch>(null);
  public currentRecords: EventEmitter<Array<InjectionDataRecord>> = new EventEmitter<Array<InjectionDataRecord>>(null);
  public getInjectionDataWorking: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public getInjectionDetailsWorking: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public getInjectionDetailsForIdComplete: EventEmitter<InjectionDataRecord> = new EventEmitter<InjectionDataRecord>();

  //Column sets for each page's data grid.

  //This one is the first page - injection volumes
  public injectionVolumeDataColumns;

  //This one is the second page, injection interval, formations, etc.
  public injectionIntervalDataColumns;

  //This one is the third page - composition of the injected material.
  public compositionDataColumns;

  constructor(httpClient: HttpClient)
  {
    super(httpClient);

    this._controllerName = "injection";
  }

  public getDataColumnsListPromise() {

    //If data columns dont exist make call to backend service
    if (!this.injectionVolumeDataColumns || !this.injectionIntervalDataColumns || !this.compositionDataColumns) {
      return this.httpClient.get("api/injection/Columns").toPromise().then((result: any) => {
        this.injectionVolumeDataColumns = result.injectionVolumeDataColumns;
        this.injectionIntervalDataColumns = result.injectionIntervalDataColumns;
        this.compositionDataColumns = result.compositionDataColumns;
        return [this.injectionVolumeDataColumns, this.injectionIntervalDataColumns, this.compositionDataColumns];
      });
    } else {
      return Promise.resolve([this.injectionVolumeDataColumns, this.injectionIntervalDataColumns, this.compositionDataColumns]);
    }

  }

  public async getCombinedDataColumnsList() {
    let labelList = [];
    let dataColumnObjs = await this.getDataColumnsListPromise();

    //interate over all dataColumns add all labels to an array
    dataColumnObjs.forEach(dataColumnObj => {
      Object.keys(dataColumnObj).forEach(key => {

        var labelProps = {
          key,
          label: dataColumnObj[key].label,
          required: dataColumnObj[key].required,
          persist: dataColumnObj[key].persist,
          type: "number",
          options: null
        };

        //Don't assign undefined to this
        if (dataColumnObj[key].type != null) {
          labelProps.type = dataColumnObj[key].type;

          //Also check if the type happens to be of the many to many type, and if so, set the options property.
          labelProps.options = dataColumnObj[key].options;
        }

        labelList.push(labelProps);
      });
    });

    return labelList;
  }

  public async GetPersistList() {
    let combinedDataColumnsList = await this.getCombinedDataColumnsList();
    return combinedDataColumnsList.filter(dataColumnObj => {
      return dataColumnObj.persist;
    }).map(dataColumnObj => {
      return dataColumnObj.key
    });
  }

  public GetInjectionData(wellId: number) {

    if (!this.getInjectionDataWorking.value) {

      var tempInjRecs = new Array<InjectionDataRecord>();

      this.currentRecords.next(tempInjRecs);

      this.getInjectionDataWorking.next(true);

      this.httpClient.get("api/injection/well/" + wellId).subscribe((result: Array<InjectionDataRecord>) => {

        this.getInjectionDataWorking.next(false);
        this.currentRecords.emit(result);
      });
    }
  }

  public ClearCurrentRecords() {
    var tempInjRecs = new Array<InjectionDataRecord>();
    this.currentRecords.emit(tempInjRecs);
  }

  public GetInjectionDetailsForId(id: number) {

    if (!this.getInjectionDataWorking.value) {

      this.getInjectionDetailsWorking.next(true);

      this.httpClient.get("api/injection/" + id).subscribe((result: InjectionDataRecord) => {
        this.getInjectionDetailsWorking.next(false);

        this.getInjectionDetailsForIdComplete.emit(result);
      });
    }
  }

  public GetLastInjectionRecordWithWellId(wellId: number): Promise<InjectionDataRecord> {

    var ret;

    if (wellId == null) {
      ret = new Promise<InjectionDataRecord>((resolve, reject) => {
        resolve(null);
      });
    }
    else {
      ret = <Promise<InjectionDataRecord>>this.httpClient.get("api/injection/LastInjectionWithWellId/" + wellId).toPromise();
    }

    return ret;
  }

  public GetEmptyDailySubmittableBatch(wellId: number, beginDate: Date, endDate: Date, useLastSubmission: boolean) {

    let lBeginDate = beginDate.getTime() / 1000;
    let lEndDate = endDate.getTime() / 1000;

    let tzo = beginDate.getTimezoneOffset();

    let tzoms = tzo * 60.0;

    let lBeginDateActuallyUtc = this.convertToActuallyUTC(beginDate, 12, 0, 0).getTime() / 1000;
    let lEndDateActuallyUtc = this.convertToActuallyUTC(endDate, 12, 0, 0).getTime() / 1000;

    let suls = "false";

    if (useLastSubmission == true) {
      suls = "true";
    }

    let url = "api/" + this._controllerName + "/Empty?wellId=" + wellId + "&begin=" + lBeginDateActuallyUtc + "&end=" + lEndDateActuallyUtc + "&useLastSubmission=" + suls;

    this.httpClient.get(url).pipe(catchError(this.handleSubmitBatchError)).subscribe( async (result: UserSubmittableDataBatch) => {

      let injBatch = <UserSubmittableDataBatch>result;
      let persistList = await this.GetPersistList();
      //add metadata values to persistList
      persistList = persistList.concat(['InjectionDate', 'InjectionEndDate', 'Id', 'WellId']);
      result.Injections = result.Injections.map(inj => {
        for (let key in inj) {
          //clear values that are not on the persist list.
          if (!persistList.includes(key)) {
            inj[key] = '';
          }
        }
        return inj;
      });

      this.currentBatch.next(injBatch);
    },
      (result) => {

      });
  }

  public GetEmptyMonthlySubmittableBatch(wellId: number, beginDate: Date, endDate: Date, useLastSubmission: boolean) {

    let lBeginDate = beginDate.getTime() / 1000;
    let lEndDate = endDate.getTime() / 1000;

    let tzo = beginDate.getTimezoneOffset();

    let tzoms = tzo * 60.0;

    let lBeginDateActuallyUtc = this.convertToActuallyUTC(beginDate, 12, 0, 0).getTime() / 1000;
    let lEndDateActuallyUtc = this.convertToActuallyUTC(endDate, 12, 0, 0).getTime() / 1000;

    let suls = "false";

    if (useLastSubmission == true) {
      suls = "true";
    }

    let url = "api/" + this._controllerName + "/EmptyMonthly?wellId=" + wellId + "&begin=" + lBeginDateActuallyUtc + "&end=" + lEndDateActuallyUtc + "&useLastSubmission=" + suls;

    this.httpClient.get(url).pipe(catchError(this.handleSubmitBatchError)).subscribe(async (result: UserSubmittableDataBatch) => {

      let injBatch = <UserSubmittableDataBatch>result;
      let persistList = await this.GetPersistList();
      //add metadata values to persistList
      persistList = persistList.concat(['InjectionDate', 'InjectionEndDate', 'Id', 'WellId']);
      result.Injections = result.Injections.map(inj => {
        for (let key in inj) {
          //clear values that are not on the persist list.
          if (!persistList.includes(key)) {
            inj[key] = '';
          }
        }
        return inj;
      });

      this.currentBatch.next(injBatch);
    },
      (result) => {

      });
  }

  public getDateStringFormat(record) {

    function areTimestampsOnSameDay(timestamp1, timestamp2) {
      // Convert timestamps to Date objects
      const date1 = new Date(timestamp1);
      const date2 = new Date(timestamp2);

      // Extract year, month, and date
      const year1 = date1.getFullYear();
      const month1 = date1.getMonth();
      const day1 = date1.getDate();

      const year2 = date2.getFullYear();
      const month2 = date2.getMonth();
      const day2 = date2.getDate();

      // Compare year, month, and date
      return year1 === year2 && month1 === month2 && day1 === day2;
    }

    let InjectionDate = record.InjectionDate;
    let InjectionEndDate = record.InjectionEndDate;

    if (areTimestampsOnSameDay(InjectionDate, InjectionEndDate)) {
      return 'yyyy/MM/dd';
    } else {
      return 'MMM yyyy';
    }

  }

}
